import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { DividerComponent } from '@shared/components/divider.component';
import { TitleContainerComponent } from '@shared/components/title-container.component';
import { QuillEditorComponent, QuillViewComponent } from 'ngx-quill';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { AuthService } from '@core/auth/auth.service';
import { SnackBarService } from '@shared/services/snack-bar.service';
import { UserGuideApiService } from '../services/user-guide-api.service';
import { UserManual } from '../models/user-manual.model';
import { MatTooltipModule } from '@angular/material/tooltip';
import { finalize, startWith, Subject, switchMap, tap } from 'rxjs';
import { IsRiskManagerDirective } from '@core/directives/is-risk-manager.directive';

@Component({
  selector: 'app-fuzzy-name-matching',
  imports: [
    TitleContainerComponent,
    MatButtonModule,
    MatIconModule,
    MatProgressBarModule,
    MatTooltipModule,
    QuillEditorComponent,
    QuillViewComponent,
    DividerComponent,
    ReactiveFormsModule,
    IsRiskManagerDirective,
  ],
  templateUrl: './fuzzy-name-matching.component.html',
  styleUrl: './fuzzy-name-matching.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FuzzyNameMatchingComponent {
  readonly textCategory = 'Fuzzy Name Matching';
  readonly maId: number = 2;

  isEditMode = signal(false);
  descriptionControl = new FormControl('');

  private _messageService = inject(SnackBarService);
  readonly userGuideApiService = inject(UserGuideApiService);
  readonly currentUser = inject(AuthService).user();
  isUserGuideLoading = signal(true);
  isLoading = computed(() => this.isUserGuideLoading());
  private readonly reloadUserGuide$ = new Subject<void>();

  // display value for quill view
  userGuideContent = toSignal(
    this.reloadUserGuide$.pipe(
      startWith(void 0), // initial trigger
      tap(() => this.isUserGuideLoading.set(true)),
      switchMap(() =>
        this.userGuideApiService.getUserGuide$(this.maId).pipe(
          tap(userGuide => {
            // Set form control value when data is received
            this.descriptionControl.setValue(userGuide?.maDefinition || '');
          }),
          finalize(() => this.isUserGuideLoading.set(false))
        )
      )
    ),
    { initialValue: null }
  );

  toggleEditMode() {
    this.isEditMode.set(!this.isEditMode());
    // control is set to the current user guide content on edit mode with this no need for clear on cancel
    if (!this.isEditMode()) {
      this.descriptionControl.setValue(this.userGuideContent()?.maDefinition ?? '');
    }
  }

  // Update User Guide
  updateManual() {
    this.isUserGuideLoading.set(true);
    const userId = this.currentUser?.userId ?? '';
    const updatedDescription = this.descriptionControl.value ?? '';

    const request: UserManual = {
      maId: this.maId,
      maUpdateId: userId,
      maDefinition: updatedDescription,
    };

    this.userGuideApiService
      .updateUserGuide$(request)
      .pipe(
        // I like to have empty subscribe if possible and handle logic in operators
        // tap is for handling side effects
        tap({
          next: () => {
            this.reloadUserGuide$.next(); // trigger reload of user guide content
            this.isEditMode.set(false);
          },
          error: () => {
            this._messageService.showError('Error: User Guide not updated!');
            this.isUserGuideLoading.set(false);
          },
        })
      )
      .subscribe();
  }
}
